/**
 * @fileoverview
 * API Endpoints Constants
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/admin-login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
  },
  USERS: {
    ME: '/users/me',
    CHANGE_PASSWORD: '/users/me/change-password',
    BY_ID: (id: string) => `/users/${id}`,
  },
  WORKSPACES: {
    BASE: '/workspaces',
    BY_ID: (id: string) => `/workspaces/${id}`,
    ACTIVATE: (id: string) => `/workspaces/${id}/activate`,
    DEACTIVATE: (id: string) => `/workspaces/${id}/deactivate`,
    DETAILS: (id: string) => `/workspaces/${id}/details`,
    DETAILS_DOCUMENT: (id: string) => `/workspaces/${id}/details/document`,
    OTF: (id: string) => `/workspaces/${id}/otf`,
    OTF_ITEM: (id: string, otfId: string) => `/workspaces/${id}/otf/${otfId}`,
    OTF_DOCUMENT: (id: string, otfId: string) =>
      `/workspaces/${id}/otf/${otfId}/document`,
    MEMBERS: (id: string) => `/workspaces/${id}/members`,
    MEMBER_ITEM: (id: string, userId: string) => `/workspaces/${id}/members/${userId}`,
    MODELS: (id: string) => `/workspaces/${id}/models`,
    MODEL_ITEM: (id: string, modelId: string) => `/workspaces/${id}/models/${modelId}`,
    MODEL_USAGE: (id: string, modelId: string) => `/workspaces/${id}/models/${modelId}/usage`,
    BILLING_SUMMARY: (id: string) => `/workspaces/${id}/billing-summary`,
    ASSIGN_PLAN: (id: string) => `/workspaces/${id}/assign-plan`,
    PLAN_HISTORY: (id: string) => `/workspaces/${id}/plan-history`,
  },
  MODEL: {
    PRODUCTS: '/model/products',
    BASE: '/model',
    BY_ID: (id: string) => `/model/${id}`,
    VERSIONS: (modelId: string) => `/model/${modelId}/versions`,
    VERSION: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}`,
    VERSION_PROGRESS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/progress`,
    TRAINING_JOB: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/training-job`,
    TRAINING_COLUMNS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/training-columns`,
    SCORING: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/scoring`,
    SCORING_PROCEED: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/scoring/proceed`,
    SCORING_REBUILD: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/scoring/rebuild`,
    TEST_DATASET: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/test-dataset`,
    TEST_DATASET_RECORDS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/test-dataset/records`,
    RULES: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/rules`,
    RULE: (modelId: string, vId: string, ruleId: string) => `/model/${modelId}/versions/${vId}/rules/${ruleId}`,
    RULES_PROCEED: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/rules/proceed`,
    RULE_TEST_RESULTS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/rule-test-results`,
    RULE_TESTING_PROCEED: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/rule-testing/proceed`,
    DEPLOY_CHECKS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/deploy/checks`,
    DEPLOYMENTS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/deployments`,
    DEPLOY: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/deploy`,
    PREDICTION_LOGS: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/prediction-logs`,
    TRAINING_DATASET_UPLOAD: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/training-dataset/upload`,
    TEST_DATASET_UPLOAD: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/test-dataset/upload`,
    ABANDON_VERSION: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/abandon`,
    PROMOTE_VERSION: (modelId: string, vId: string) => `/model/${modelId}/versions/${vId}/promote`,
    ROLLBACK: (modelId: string) => `/model/${modelId}/rollback`,
    MODEL_DEPLOYMENTS: (modelId: string) => `/model/${modelId}/deployments`,
    AUDIT_LOGS: (modelId: string) => `/model/${modelId}/audit-logs`,
    ML_SERVICE_HEALTH: '/model/ml-service/health',
  },

  GLOBAL_SETTINGS: {
    SMTP_SETTINGS: '/super-admin/smtp-settings',
    UPDATE_SMTP_SETTINGS: '/super-admin/smtp-settings',
    EMAIL_TEMPLATES: '/super-admin/email-templates',
    EMAIL_TEMPLATE_BY_ID: (id: number | string) => `/super-admin/email-templates/${id}`,
    ORGANIZATION: '/super-admin/organization',
    ORGANIZATION_BY_ID: (id: number | string) => `/super-admin/organization/${id}`,
    AUTHORIZATIONS: '/super-admin/authorizations',
    AUTHORIZATIONS_BY_ID: (id: number | string) => `/super-admin/authorizations/${id}`,
  },

  INVOICES: {
    LIST: '/billing/invoices',
    SUMMARY: '/billing/dashboard/summary',
    LIST_BY_ID: (workspaceId: string) => `/billing/workspace/${workspaceId}/detailInvoice`,
    BY_ID: (invoiceId: string) => `/billing/invoices/${invoiceId}/calculate-detailed-amount`,
    GENERATE_DATA: (invoiceNo: string) => `/billing/invoices/${invoiceNo}/generate-data`,
    DOWNLOAD: (invoiceNo: string) => `/billing/invoices/${invoiceNo}/download`,
    VALIDATE_PAYMENT_AMOUNT: '/billing/invoices/validate-payment-amount',
    CALCULATE_TDS: '/billing/invoices/calculate-tds',
    RECORD_PAYMENT: '/billing/invoices/record-payment',
    PAYMENT_HISTORY: (invoiceNo: string) => `/billing/invoices/${invoiceNo}/payments`,
  },

  AUDIT_TRAIL: {
    // LIST: '/audit-trail',
    LIST: '/audit-trail/search',
    SUMMARY: '/audit-trail/summary',
    SUMMARY_SEARCH: '/audit-trail/summary/search',
    FILTERS: '/audit-trail/filter-options',
    EXPORT: '/audit-trail/export',
    BY_ID: (logId: string) => `/audit-trail/${logId}`,
    SUMMARY_EXPORT: (logId: string) => `/audit-trail/${logId}/export`,
  },

  DASHBOARD: {
    REVENUE_SUMMARY: (year: string) => `/billing/aggregate/revenue?year=${year}`,
    INVOICE_RATIO_SUMMARY:(year: string) => `/billing/aggregate/invoice-ratio?year=${year}`,
    BUSINESS_SUMMARY: (year: string) => `/billing/aggregate/active-inactive?year=${year}`,
    SERVICE_USED_SUMMARY: (year: string) => `/billing/aggregate/payment-types?year=${year}`,
  },

  SUBSCRIPTION: {
    PREPAID_PLANS: '/prepaid-plans/getPrepaidPlans',
    CREATE_PREPAID_PLAN: '/prepaid-plans/createPrepaidPlan',
    UPDATE_PREPAID_PLAN_STATUS: (planId: string) => `/prepaid-plans/updatePrepaidPlanStatus/${planId}`,
  },

  ADD_ON_PLANS: {
    CREATE: '/addon-plans/createAddonPlan',
    LIST: '/addon-plans/getAddonPlans',
    BY_ID: (id: string) => `/addon-plans/getAddonPlanById/${id}`,
    UPDATE: (id: string) => `/addon-plans/updateAddonPlan/${id}`,
    UPDATE_STATUS: (id: string) => `/addon-plans/updateAddonPlanStatus/${id}`,
    DELETE: (id: string) => `/addon-plans/deleteAddonPlan/${id}`,
  },
};